<template>
  <div class="coupon-usage-log-container" @click="closeOverlays">
    <h1 class="page-title">Coupon Usage Log</h1>

    <div class="filter-section">
      <div class="filter-row">
        <div
          class="search-wrapper field-search-item"
          :class="{ 'is-open': openDd === 'searchField' }"
        >
          <div
            class="search-select-wrapper"
            :class="{ 'is-open': openDd === 'searchField' }"
            @click.stop="toggleSearchFieldDropdown"
          >
            <span class="search-select-label">{{ currentSearchFieldLabel }}</span>
            <img :src="downarrowIcon" alt="" class="dropdown-arrow" />
          </div>
          <input
            v-model="filters.searchQuery"
            type="text"
            class="search-input"
            placeholder="Search.."
            maxlength="50"
            @keyup.enter="handleSearch"
            @focus="openDd = null"
          />
          <div v-if="openDd === 'searchField'" class="dropdown-menu" @click.stop>
            <div
              v-for="option in searchFieldOptions"
              :key="option.value"
              class="dropdown-item"
              :class="{ selected: filters.searchField === option.value }"
              @click="selectSearchField(option.value)"
            >
              {{ option.label }}
            </div>
          </div>
        </div>

        <div class="filter-box filter-box--date" :class="{ 'is-open': openCal }">
          <span class="filter-label-inline">Usage Date:</span>
          <div class="date-calendar-anchor">
            <div class="date-input-wrap" @click.stop="toggleCal">
              <input
                type="text"
                readonly
                class="filter-date-input"
                :value="dateDisplay"
                placeholder="Select a date"
                :class="{ 'is-placeholder': !filters.usageDate }"
              />
              <span class="date-cal-split" aria-hidden="true" />
              <img :src="calendarIcon" alt="" class="calendar-icon" />
            </div>
            <div v-if="openCal" class="calendar-pop" @click.stop>
              <CalendarMini
                v-model:month="calMonth"
                v-model:year="calYear"
                :selected-ymd="filters.usageDate"
                @pick="onPickDate"
              />
            </div>
          </div>
        </div>

        <div class="filter-actions-inline">
          <button class="btn-search" type="button" @click="handleSearch">Search</button>
          <button class="btn-clear" type="button" @click="handleClear">Clear</button>
        </div>
      </div>
    </div>

    <div class="records-bar">
      <span class="count-info" :class="{ 'count-info--pending': pageView === 'loading' }">
        {{ pageView === 'loading' ? 0 : totalCount }} Records
      </span>
    </div>

    <div class="list-body" :aria-busy="pageView === 'loading'">
      <LoadingIndicator v-if="pageView === 'loading'" />
      <div v-else-if="totalCount === 0" class="orders-empty">
        <img :src="imgListBlank" class="orders-empty-img" alt="" />
        <p class="orders-empty-msg">No records found</p>
      </div>
      <div v-else class="table-section">
        <table class="usage-table">
          <thead>
            <tr>
              <th>Coupon Code</th>
              <th>Discount Amount</th>
              <th>Final Amount Paid</th>
              <th>Applied Order ID</th>
              <th>User Mobile</th>
              <th>Usage Time</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in displayRows" :key="row.id">
              <td>
                <button type="button" class="cell-link" @click.stop="handleCouponCodeClick(row)">
                  {{ row.couponCode }}
                </button>
              </td>
              <td>{{ row.discountAmount }}</td>
              <td>{{ row.finalAmountPaid }}</td>
              <td>
                <button type="button" class="cell-link" @click.stop="handleOrderClick(row)">
                  {{ row.appliedOrderId }}
                </button>
              </td>
              <td>
                <button type="button" class="cell-link" @click.stop="handleMobileClick(row)">
                  {{ row.userMobile }}
                </button>
              </td>
              <td>{{ row.usageTime }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="pagination-wrapper">
      <PageComponent
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="handlePageChange"
      />
    </div>

    <Teleport to="body">
      <CouponDetail
        v-if="showCouponDetail && currentCouponId"
        :coupon-id="currentCouponId"
        @close="handleCouponDetailClose"
        @set-visibility="handleDetailSetVisibility"
        @request-status="handleDetailRequestStatus"
        @updated="handleDetailUpdated"
      />

      <CenterActivate
        v-if="showStatusModal && statusCouponId"
        :coupon-id="statusCouponId"
        :record-id="statusRecordId"
        :coupon-name="statusCouponName"
        :action="statusAction"
        @close="handleStatusClose"
        @saved="handleStatusSaved"
        @failed="handleStatusFailed"
      />

      <SetVisibility
        v-if="showVisibilityModal && visibilityCouponId"
        :coupon-id="visibilityCouponId"
        :record-id="visibilityRecordId"
        :initial-visibility="visibilityInitial"
        @close="handleVisibilityClose"
        @saved="handleVisibilitySaved"
        @failed="handleVisibilityFailed"
      />

      <UserDetail
        v-if="showUserDetail && currentUserId"
        :user-id="currentUserId"
        @close="handleUserDetailClose"
      />

      <div v-if="orderDetailOverlayOrder" class="cod-overlay-root">
        <div class="cod-overlay-shell">
          <CareOrderDetailPanel
            :order="orderDetailOverlayOrder"
            @close="closeOrderDetailOverlay"
            @add-note="onDetailPanelAddNote"
            @schedule="onDetailPanelSchedule"
            @reschedule="onDetailPanelReschedule"
            @refund="onDetailPanelRefund"
          />
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="noteModalOpen"
        class="note-modal-root"
        role="presentation"
      >
        <div class="note-modal" role="dialog" aria-modal="true" aria-labelledby="note-modal-title">
          <header class="note-modal-head">
            <div class="note-modal-head-top">
              <h2 id="note-modal-title">Add Notes</h2>
              <button
                type="button"
                class="note-modal-x"
                aria-label="Close"
                :disabled="noteSaving"
                @click="closeNoteModal"
              >
                <img :src="imgClose" class="note-modal-x-ic" alt="" />
              </button>
            </div>
            <label class="note-modal-label" for="coupon-usage-note-input">Notes</label>
          </header>
          <div class="note-modal-body">
            <textarea
              id="coupon-usage-note-input"
              :value="noteDraft"
              class="note-modal-textarea"
              placeholder="Add notes"
              @input="onNoteDraftInput"
              @paste="onNoteDraftPaste"
            />
          </div>
          <footer class="note-modal-foot">
            <button
              type="button"
              class="note-modal-btn note-modal-btn-outline"
              :disabled="noteSaving"
              @click="closeNoteModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="note-modal-btn note-modal-btn-solid"
              :disabled="noteSaving"
              @click="confirmNoteModal"
            >
              Submit
            </button>
          </footer>
        </div>
      </div>
    </Teleport>

    <RefundModal
      :visible="refundModalOpen"
      :order-primary-id="refundOrderPrimaryId"
      :line-index="refundTarget?.lineIndex ?? 0"
      :amount-paid="refundAmountPaid"
      :refundable-amount="refundableAmountDisplay"
      :refunded-amount="refundedAmountDisplay"
      :tax-rate="refundTaxRate"
      :tax-options="refundTaxOptions"
      :submitting="refundSaving"
      @close="closeRefundModal"
      @submit="confirmRefundModal"
      @toast="onRefundToast"
    />

    <Teleport to="body">
      <CareOrderSchedule
        v-if="scheduleOverlayOpen && scheduleOverlayFocus"
        :embedded="true"
        :initial-focus="scheduleOverlayFocus"
        @close="onScheduleOverlayClose"
        @orders-updated="onScheduleOrdersUpdated"
      />
    </Teleport>

    <Teleport to="body">
      <ToastComponent :visible="toast.visible" :type="toast.type" :message="toast.message" />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, provide, reactive, ref } from 'vue'
import PageComponent from '@/components/PageComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import ToastComponent from '@/components/ToastComponent.vue'
import CalendarMini from '@/views/Home/components/CalendarMini.vue'
import CareOrderDetailPanel from '@/views/Home/components/CareOrderDetailPanel.vue'
import CareOrderSchedule from '@/views/care-order-schedule/CareOrderSchedule.vue'
import type { CareScheduleFocusPayload } from '@/views/care-order-schedule/types'
import RefundModal from '@/views/Home/components/RefundModal.vue'
import type { RefundSubmitPayload, RefundTaxOption } from '@/views/Home/components/RefundModal.vue'
import CouponDetail, {
  type CouponDetailRequestStatusPayload,
  type CouponDetailSetVisibilityPayload,
  type CouponDetailUpdatePayload,
} from '@/views/CouponsPage/CouponDetail.vue'
import CenterActivate from '@/views/CouponsPage/CenterActivate.vue'
import SetVisibility from '@/views/CouponsPage/SetVisibility.vue'
import UserDetail from '@/views/userspage/components/UserDetail.vue'
import { TOAST_DURATION } from '@/constants/toast'
import { FIELD_LIMITS } from '@/constants/fieldLimits'
import {
  buildCouponUsageLogParams,
  mapCouponUsageLogItem,
  postCouponUsageList,
  resolveCouponDetailId,
  resolveUserDetailId,
  type CouponUsageLogRow,
} from '@/api/CouponUsageLog'
import {
  buildUsageSearchTypes,
  DEFAULT_USAGE_SEARCH_TYPES,
  postCouponUsageMeta,
  type CouponUsageMetaOption,
} from '@/api/CouponUsageMeta'
import { isFollowUpCareLine } from '@/api/careOrderLineDisplay'
import {
  buildRefundTaxOptions,
  formatRefundMoney,
  parseRefundTaxRate,
  postCareOrderRefund,
  postCareOrderRefundContext,
  postCareOrderRefundMeta,
  DEFAULT_REFUND_TAX_OPTIONS,
} from '@/api/careOrderRefund'
import {
  careOrderListItemToCardDto,
  extractCareOrderListArray,
  parseCareOrderListResponse,
  postCareOrderDetail,
  postCareOrderUpdateNotes,
  postCareScheduleContext,
  readCareOrderNumericId,
  resolveCareOrderUpdateNotesTarget,
  type OrderCardDTO,
  type OrderServiceLineDTO,
} from '@/api/orders'
import { fetchCareOrderListCached } from '@/utils/careOrderListCache'
import { fieldLimitError, validateStringLength } from '@/utils/fieldLimitValidation'
import { mapCareOrderListToCards } from '@/utils/technicianNoFromList'
import calendarIcon from '@/assets/编组 2@2x.png'
import downarrowIcon from '@/assets/downarrow.png'
import imgClose from '@/assets/Fill 1@2x.png'
import imgListBlank from '@/assets/list_blank@2x.png'

const orderFieldLimits = FIELD_LIMITS.order

const toast = reactive({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
})

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  toast.type = type
  toast.message = message
  toast.visible = true
  window.setTimeout(() => {
    toast.visible = false
  }, TOAST_DURATION.brief)
}

provide<(type: 'success' | 'warning' | 'error', message: string) => void>('showToast', showToast)

const PAGE_SIZE = 10
const LOADING_MIN_MS = 1500

type PageView = 'loading' | 'content'

const now = new Date()
const calMonth = ref(now.getMonth())
const calYear = ref(now.getFullYear())
const openCal = ref(false)
const openDd = ref<'searchField' | null>(null)
const currentPage = ref(1)
const totalCount = ref(0)
const totalPages = ref(1)
const pageView = ref<PageView>('loading')
let fetchGeneration = 0
const displayRows = ref<CouponUsageLogRow[]>([])
const searchFieldOptions = ref<CouponUsageMetaOption[]>([...DEFAULT_USAGE_SEARCH_TYPES])
const showCouponDetail = ref(false)
const currentCouponId = ref('')
const showStatusModal = ref(false)
const statusCouponId = ref('')
const statusRecordId = ref(0)
const statusCouponName = ref('')
const statusAction = ref<'deactivate' | 'reactivate'>('deactivate')
const showVisibilityModal = ref(false)
const visibilityCouponId = ref('')
const visibilityRecordId = ref(0)
const visibilityInitial = ref<'visible' | 'hidden'>('visible')
const showUserDetail = ref(false)
const currentUserId = ref('')
const orderDetailOverlayOrder = ref<OrderCardDTO | null>(null)
const detailResolving = ref(false)

const scheduleOverlayOpen = ref(false)
const scheduleOverlayFocus = ref<CareScheduleFocusPayload | null>(null)

const noteModalOpen = ref(false)
const noteDraft = ref('')
const noteTarget = ref<{ order: OrderCardDTO; lineIndex: number } | null>(null)
const noteSaving = ref(false)
let lastNoteLimitToastAt = 0

type RefundTarget = {
  order: OrderCardDTO
  line: OrderServiceLineDTO
  lineIndex: number
}

const refundModalOpen = ref(false)
const refundTarget = ref<RefundTarget | null>(null)
const refundSaving = ref(false)
const refundContextLoading = ref(false)
const refundAmountPaid = ref('$0.00')
const refundableAmountDisplay = ref('$0.00')
const refundedAmountDisplay = ref('$0.00')
const refundTaxRate = ref(0)
const refundTaxOptions = ref<RefundTaxOption[]>([...DEFAULT_REFUND_TAX_OPTIONS])

const refundOrderPrimaryId = computed(() => {
  const pk = refundTarget.value?.order.orderPrimaryId
  return pk != null && Number.isFinite(pk) && pk > 0 ? pk : null
})

const filters = reactive({
  searchField: 'coupon_code',
  searchQuery: '',
  usageDate: '',
})

const appliedFilters = reactive({
  searchField: 'coupon_code',
  searchQuery: '',
  usageDate: '',
})

const currentSearchFieldLabel = computed(() => {
  const hit = searchFieldOptions.value.find((option) => option.value === filters.searchField)
  return hit?.label ?? 'Coupon Code'
})

function ensureValidSearchField(): void {
  const values = searchFieldOptions.value.map((option) => option.value)
  if (!values.includes(filters.searchField)) {
    const fallback = searchFieldOptions.value[0]?.value ?? 'coupon_code'
    filters.searchField = fallback
    appliedFilters.searchField = fallback
  }
}

async function fetchUsageMeta(): Promise<void> {
  try {
    const response = await postCouponUsageMeta()
    if (response.code === 200) {
      searchFieldOptions.value = buildUsageSearchTypes(response.data)
      ensureValidSearchField()
    }
  } catch (error) {
    console.error('Fetch coupon usage meta error:', error)
  }
}

const dateDisplay = computed(() => {
  if (!filters.usageDate) return ''
  const [y, m, d] = filters.usageDate.split('-').map(Number)
  if (!y || !m || !d) return filters.usageDate
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
})

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchUsageList(): Promise<void> {
  const generation = ++fetchGeneration
  pageView.value = 'loading'
  try {
    const [response] = await Promise.all([
      postCouponUsageList(
        buildCouponUsageLogParams({
          page: currentPage.value,
          pageSize: PAGE_SIZE,
          searchField: appliedFilters.searchField,
          searchQuery: appliedFilters.searchQuery,
          usageDate: appliedFilters.usageDate,
        }),
      ),
      delay(LOADING_MIN_MS),
    ])

    if (generation !== fetchGeneration) return

    if (response.code === 200 && response.data) {
      displayRows.value = (response.data.list ?? []).map(mapCouponUsageLogItem)
      totalCount.value = response.data.pagination?.total ?? displayRows.value.length
      totalPages.value = Math.max(1, response.data.pagination?.total_pages ?? 1)
      return
    }

    displayRows.value = []
    totalCount.value = 0
    totalPages.value = 1
  } catch (error) {
    if (generation !== fetchGeneration) return
    console.error('Fetch coupon usage list error:', error)
    displayRows.value = []
    totalCount.value = 0
    totalPages.value = 1
  } finally {
    if (generation === fetchGeneration) {
      pageView.value = 'content'
    }
  }
}

function closeOverlays(): void {
  openCal.value = false
  openDd.value = null
}

function toggleSearchFieldDropdown(): void {
  openCal.value = false
  openDd.value = openDd.value === 'searchField' ? null : 'searchField'
}

function selectSearchField(value: string): void {
  filters.searchField = value
  openDd.value = null
}

function toggleCal(): void {
  openDd.value = null
  openCal.value = !openCal.value
}

function onPickDate(ymd: string): void {
  filters.usageDate = ymd
  openCal.value = false
}

function handleSearch(): void {
  appliedFilters.searchField = filters.searchField
  appliedFilters.searchQuery = filters.searchQuery
  appliedFilters.usageDate = filters.usageDate
  currentPage.value = 1
  openDd.value = null
  void fetchUsageList()
}

function handleClear(): void {
  filters.searchField = 'coupon_code'
  filters.searchQuery = ''
  filters.usageDate = ''
  appliedFilters.searchField = 'coupon_code'
  appliedFilters.searchQuery = ''
  appliedFilters.usageDate = ''
  currentPage.value = 1
  openCal.value = false
  openDd.value = null
  void fetchUsageList()
}

function handlePageChange(page: number): void {
  currentPage.value = page
  void fetchUsageList()
}

function orderDisplayIdMatches(order: OrderCardDTO, orderId: string): boolean {
  const key = orderId.trim().toLowerCase()
  if (!key) return false
  const id = String(order.id ?? '').trim().toLowerCase()
  if (id === key) return true
  const pk = order.orderPrimaryId
  if (pk != null && Number.isFinite(pk)) {
    if (String(pk).toLowerCase() === key) return true
    if (`hm-${pk}`.toLowerCase() === key) return true
  }
  return false
}

function parseOrderListFromRaw(raw: unknown): OrderCardDTO[] {
  if (raw != null && typeof raw === 'object') {
    const response = raw as Record<string, unknown>
    if (response.code === 200 && response.data != null && typeof response.data === 'object') {
      const listRaw = extractCareOrderListArray(response.data as Record<string, unknown>)
      if (listRaw) return mapCareOrderListToCards(listRaw, null)
    }
  }
  const parsed = parseCareOrderListResponse(raw)
  return parsed?.list ?? []
}

function pickPrimaryIdFromRawList(raw: unknown, orderId: string): number | undefined {
  if (raw == null || typeof raw !== 'object') return undefined
  const response = raw as Record<string, unknown>
  const data = response.data
  if (data == null || typeof data !== 'object') return undefined
  const listRaw = extractCareOrderListArray(data as Record<string, unknown>)
  if (!listRaw?.length) return undefined

  const key = orderId.trim().toLowerCase()
  for (const item of listRaw) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const displayId = String(row.order_id ?? '').trim().toLowerCase()
    if (key && displayId && displayId !== key && !displayId.includes(key) && !key.includes(displayId)) {
      continue
    }
    const pk =
      readCareOrderNumericId(row.id) ??
      readCareOrderNumericId(row.order_primary_id) ??
      readCareOrderNumericId(row.care_order_id)
    if (pk != null) return pk
  }

  const first = listRaw[0]
  if (first && typeof first === 'object') {
    const row = first as Record<string, unknown>
    return (
      readCareOrderNumericId(row.id) ??
      readCareOrderNumericId(row.order_primary_id) ??
      readCareOrderNumericId(row.care_order_id)
    )
  }
  return undefined
}

async function fetchOrderCardForRow(row: CouponUsageLogRow): Promise<OrderCardDTO | null> {
  if (row.orderPrimaryId) {
    try {
      const detail = await postCareOrderDetail(row.orderPrimaryId)
      return careOrderListItemToCardDto(detail)
    } catch (error) {
      console.warn('Fetch order detail by primary id failed:', error)
    }
  }

  const orderId = row.appliedOrderId.trim()
  if (!orderId) return null

  try {
    const raw = await fetchCareOrderListCached({
      page: 1,
      pageSize: 20,
      searchField: 'order_id',
      searchQuery: orderId,
    })
    const list = parseOrderListFromRaw(raw)
    let card = list.find((order) => orderDisplayIdMatches(order, orderId)) ?? list[0] ?? null

    if (card && (card.orderPrimaryId == null || card.orderPrimaryId <= 0)) {
      const pk = pickPrimaryIdFromRawList(raw, orderId)
      if (pk != null) {
        card = { ...card, orderPrimaryId: pk }
      }
    }

    if (!card?.orderPrimaryId) return null

    try {
      const detail = await postCareOrderDetail(card.orderPrimaryId)
      return careOrderListItemToCardDto(detail)
    } catch (error) {
      console.warn('Fetch order detail after list resolve failed:', error)
      return card
    }
  } catch (error) {
    console.error('Fetch order card error:', error)
    return null
  }
}

async function handleCouponCodeClick(row: CouponUsageLogRow): Promise<void> {
  if (detailResolving.value) return
  detailResolving.value = true
  try {
    const couponId = await resolveCouponDetailId(row)
    if (!couponId) {
      showToast('warning', 'Coupon not found.')
      return
    }
    currentCouponId.value = couponId
    showCouponDetail.value = true
  } finally {
    detailResolving.value = false
  }
}

function handleCouponDetailClose(): void {
  showCouponDetail.value = false
  currentCouponId.value = ''
}

function handleDetailRequestStatus(payload: CouponDetailRequestStatusPayload): void {
  if (!currentCouponId.value || !payload.recordId) {
    showToast('error', 'Coupon record ID is missing')
    return
  }
  statusCouponId.value = currentCouponId.value
  statusRecordId.value = payload.recordId
  statusCouponName.value = payload.couponName
  statusAction.value = payload.action
  showStatusModal.value = true
}

function handleStatusClose(): void {
  showStatusModal.value = false
  statusCouponId.value = ''
  statusRecordId.value = 0
  statusCouponName.value = ''
}

function handleStatusSaved(): void {
  const action = statusAction.value
  const message =
    action === 'deactivate' ? 'Successfully deactivated' : 'Successfully reactivated'
  handleStatusClose()
  handleCouponDetailClose()
  void nextTick(() => {
    showToast('success', message)
  })
}

function handleStatusFailed(): void {
  handleStatusClose()
}

function handleDetailUpdated(payload: CouponDetailUpdatePayload): void {
  const message =
    payload.availability === 'inactive' ? 'Successfully deactivated' : 'Successfully reactivated'
  handleCouponDetailClose()
  void nextTick(() => {
    showToast('success', message)
  })
}

function handleDetailSetVisibility(payload: CouponDetailSetVisibilityPayload): void {
  if (!currentCouponId.value || !payload.recordId) {
    showToast('error', 'Coupon record ID is missing')
    return
  }
  visibilityCouponId.value = currentCouponId.value
  visibilityRecordId.value = payload.recordId
  visibilityInitial.value = payload.visibility
  showVisibilityModal.value = true
}

function handleVisibilityClose(): void {
  showVisibilityModal.value = false
  visibilityCouponId.value = ''
  visibilityRecordId.value = 0
}

function handleVisibilitySaved(): void {
  showVisibilityModal.value = false
  visibilityCouponId.value = ''
  visibilityRecordId.value = 0
  handleCouponDetailClose()
  void nextTick(() => {
    showToast('success', 'Successfully set')
  })
}

function handleVisibilityFailed(): void {
  // 错误提示由 SetVisibility 弹窗内 toast 展示
}

async function handleOrderClick(row: CouponUsageLogRow): Promise<void> {
  if (detailResolving.value) return
  const orderId = row.appliedOrderId.trim()
  if (!orderId && !row.orderPrimaryId) {
    showToast('warning', 'Order not found.')
    return
  }

  detailResolving.value = true
  try {
    const order = await fetchOrderCardForRow(row)
    if (!order) {
      showToast('warning', 'Order not found.')
      return
    }
    orderDetailOverlayOrder.value = order
  } finally {
    detailResolving.value = false
  }
}

function closeOrderDetailOverlay(): void {
  orderDetailOverlayOrder.value = null
}

function apiErrorMessage(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const data = (e as { response?: { data?: { msg?: string; message?: string } } }).response?.data
    if (data?.msg) return data.msg
    if (data?.message) return data.message
  }
  if (e instanceof Error && e.message) return e.message
  return 'Request failed. Please try again.'
}

function pickActionTarget(
  order: OrderCardDTO,
): { line: OrderServiceLineDTO; lineIndex: number } | null {
  if (!order.services.length) return null
  const mainIdx = order.services.findIndex((line) => !isFollowUpCareLine(line))
  const lineIndex = mainIdx >= 0 ? mainIdx : 0
  const line = order.services[lineIndex]
  return line ? { line, lineIndex } : null
}

async function resolveActionOrder(order: OrderCardDTO): Promise<OrderCardDTO> {
  if (order.services.length) return order
  const pk = order.orderPrimaryId
  if (pk == null || !Number.isFinite(pk) || pk <= 0) return order
  try {
    const fresh = careOrderListItemToCardDto(await postCareOrderDetail(pk))
    if (orderDetailOverlayOrder.value?.id === order.id) {
      orderDetailOverlayOrder.value = fresh
    }
    return fresh
  } catch {
    return order
  }
}

function serviceCycleYmdForScheduleFocus(order: OrderCardDTO, lineIndex: number): string {
  const fromOrder = order.serviceDate?.trim()
  if (fromOrder) {
    const match = fromOrder.match(/(\d{4}-\d{2}-\d{2})/)
    if (match?.[1]) return match[1]
  }
  const line = order.services[lineIndex] ?? order.services[0]
  const raw = (line?.scheduledAt ?? '').trim()
  const match = raw.match(/(\d{4}-\d{2}-\d{2})/)
  return match?.[1] ?? ''
}

function todayYmdForScheduleContext(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function buildScheduleFocusPayload(
  order: OrderCardDTO,
  line: OrderServiceLineDTO,
  lineIndex: number,
  mode: 'schedule' | 'reschedule',
): CareScheduleFocusPayload {
  return {
    orderId: order.id,
    lineIndex,
    serviceName: line.serviceName,
    address: line.address,
    homeId: order.homeId,
    serviceCycleDate: serviceCycleYmdForScheduleFocus(order, lineIndex),
    orderList: [order],
    mode,
    followUpPrimaryId:
      isFollowUpCareLine(line) && line.followUpPrimaryId != null && line.followUpPrimaryId > 0
        ? Math.trunc(line.followUpPrimaryId)
        : undefined,
    rescheduleKind: mode === 'reschedule' ? 'proactive' : undefined,
  }
}

async function ensureRescheduleContextAllowed(
  order: OrderCardDTO,
  lineIndex: number,
): Promise<boolean> {
  const target = await resolveCareOrderUpdateNotesTarget(order, lineIndex, postCareOrderDetail)
  if (!target) {
    showToast('error', 'Cannot reschedule: order id is missing.')
    return false
  }
  try {
    await postCareScheduleContext({
      target_type: target.target_type,
      target_id: target.target_id,
      week_start: todayYmdForScheduleContext(),
    })
    return true
  } catch (e: unknown) {
    showToast('warning', apiErrorMessage(e))
    return false
  }
}

async function openScheduleOverlay(
  order: OrderCardDTO,
  mode: 'schedule' | 'reschedule',
): Promise<void> {
  let focusOrder = await resolveActionOrder(order)
  const target = pickActionTarget(focusOrder)
  if (!target) {
    showToast('warning', 'No service line available for schedule.')
    return
  }
  if (mode === 'reschedule') {
    const allowed = await ensureRescheduleContextAllowed(focusOrder, target.lineIndex)
    if (!allowed) return
  }

  const pk = focusOrder.orderPrimaryId
  if (pk != null && Number.isFinite(pk) && pk > 0) {
    try {
      focusOrder = careOrderListItemToCardDto(await postCareOrderDetail(pk))
      if (orderDetailOverlayOrder.value?.id === order.id) {
        orderDetailOverlayOrder.value = focusOrder
      }
    } catch {
      /* keep focusOrder */
    }
  }

  const focusTarget = pickActionTarget(focusOrder) ?? target
  scheduleOverlayFocus.value = buildScheduleFocusPayload(
    focusOrder,
    focusTarget.line,
    focusTarget.lineIndex,
    mode,
  )
  scheduleOverlayOpen.value = true
}

function onDetailPanelSchedule(order: OrderCardDTO): void {
  void openScheduleOverlay(order, 'schedule')
}

function onDetailPanelReschedule(order: OrderCardDTO): void {
  void openScheduleOverlay(order, 'reschedule')
}

function onScheduleOverlayClose(patchesFromClose?: OrderCardDTO[]): void {
  scheduleOverlayOpen.value = false
  scheduleOverlayFocus.value = null
  if (patchesFromClose?.length) onScheduleOrdersUpdated(patchesFromClose)
}

function onScheduleOrdersUpdated(patches: OrderCardDTO[]): void {
  if (!patches.length) return
  const current = orderDetailOverlayOrder.value
  if (!current) return
  const hit = patches.find((item) => item.id === current.id)
  if (hit) orderDetailOverlayOrder.value = hit
}

function showNoteLimitToast(): void {
  const nowTs = Date.now()
  if (nowTs - lastNoteLimitToastAt < 1200) return
  lastNoteLimitToastAt = nowTs
  showToast('warning', fieldLimitError('Notes', orderFieldLimits.notes))
}

function onNoteDraftInput(e: Event): void {
  const el = e.target as HTMLTextAreaElement
  const limit = orderFieldLimits.notes
  let next = el.value
  if (next.length > limit) {
    next = next.slice(0, limit)
    el.value = next
    showNoteLimitToast()
  }
  noteDraft.value = next
}

function onNoteDraftPaste(e: ClipboardEvent): void {
  const paste = e.clipboardData?.getData('text') ?? ''
  if (!paste) return
  const limit = orderFieldLimits.notes
  const el = e.target as HTMLTextAreaElement
  const start = el.selectionStart ?? noteDraft.value.length
  const end = el.selectionEnd ?? start
  const merged = noteDraft.value.slice(0, start) + paste + noteDraft.value.slice(end)
  if (merged.length <= limit) return
  e.preventDefault()
  noteDraft.value = merged.slice(0, limit)
  showNoteLimitToast()
}

function onDetailPanelAddNote(order: OrderCardDTO): void {
  const target = pickActionTarget(order)
  noteTarget.value = { order, lineIndex: target?.lineIndex ?? 0 }
  noteDraft.value = order.notes || ''
  noteModalOpen.value = true
}

function closeNoteModal(): void {
  if (noteSaving.value) return
  noteModalOpen.value = false
  noteTarget.value = null
}

async function refreshOverlayOrder(order: OrderCardDTO): Promise<void> {
  const pk = order.orderPrimaryId
  if (pk == null || !Number.isFinite(pk) || pk <= 0) return
  try {
    orderDetailOverlayOrder.value = careOrderListItemToCardDto(await postCareOrderDetail(pk))
  } catch (e: unknown) {
    console.error('Refresh order detail after action failed:', e)
  }
}

async function confirmNoteModal(): Promise<void> {
  if (noteSaving.value) return
  const t = noteTarget.value
  if (!t) return
  const trimmed = noteDraft.value.trim()
  if (!trimmed) {
    showToast('warning', 'Please enter note text.')
    return
  }
  const notesLimitErr = validateStringLength(trimmed, orderFieldLimits.notes, 'Notes')
  if (notesLimitErr) {
    showToast('warning', notesLimitErr)
    return
  }
  const { order, lineIndex } = t
  const target = await resolveCareOrderUpdateNotesTarget(order, lineIndex, postCareOrderDetail)
  if (!target) {
    showToast('error', 'Cannot save notes: order id is missing.')
    return
  }

  noteSaving.value = true
  try {
    await postCareOrderUpdateNotes({
      target_type: target.target_type,
      target_id: target.target_id,
      notes: trimmed,
    })
    if (orderDetailOverlayOrder.value?.id === order.id) {
      orderDetailOverlayOrder.value = { ...orderDetailOverlayOrder.value, notes: trimmed }
    }
    noteModalOpen.value = false
    noteTarget.value = null
    showToast('success', 'Successfully saved')
    await refreshOverlayOrder(order)
  } catch (e: unknown) {
    showToast(
      'warning',
      `${apiErrorMessage(e)} Your note is shown on the card only until the server accepts it.`,
    )
  } finally {
    noteSaving.value = false
  }
}

function resetRefundContextDisplay(): void {
  refundAmountPaid.value = '$0.00'
  refundableAmountDisplay.value = '$0.00'
  refundedAmountDisplay.value = '$0.00'
  refundTaxRate.value = 0
  refundTaxOptions.value = [...DEFAULT_REFUND_TAX_OPTIONS]
}

async function loadRefundContextForTarget(
  order: OrderCardDTO,
  lineIndex: number,
): Promise<boolean> {
  const pk = order.orderPrimaryId
  if (pk == null || !Number.isFinite(pk) || pk <= 0) {
    resetRefundContextDisplay()
    showToast('error', 'Order id missing for refund')
    return false
  }

  refundContextLoading.value = true
  try {
    const [metaRes, contextRes] = await Promise.all([
      postCareOrderRefundMeta({ id: pk, line_index: lineIndex }),
      postCareOrderRefundContext({ id: pk, line_index: lineIndex }),
    ])

    if (metaRes.code === 200) {
      refundTaxOptions.value = buildRefundTaxOptions(metaRes.data)
      const metaRate = parseRefundTaxRate(metaRes.data?.tax_rate)
      if (metaRate > 0) refundTaxRate.value = metaRate
    } else {
      refundTaxOptions.value = [...DEFAULT_REFUND_TAX_OPTIONS]
    }

    if (contextRes.code === 200 && contextRes.data) {
      const data = contextRes.data
      refundAmountPaid.value = formatRefundMoney(data.amount_paid)
      refundableAmountDisplay.value = formatRefundMoney(data.refundable_amount)
      refundedAmountDisplay.value = formatRefundMoney(data.refunded_amount)
      const ctxRate = parseRefundTaxRate(data.tax_rate)
      if (ctxRate > 0) refundTaxRate.value = ctxRate
      return true
    }

    resetRefundContextDisplay()
    showToast('error', contextRes.msg || 'Failed to load refund context')
    return false
  } catch (e: unknown) {
    console.error('Failed to load refund context:', e)
    resetRefundContextDisplay()
    showToast('error', apiErrorMessage(e))
    return false
  } finally {
    refundContextLoading.value = false
  }
}

async function openRefundModal(order: OrderCardDTO): Promise<void> {
  const resolved = await resolveActionOrder(order)
  const target = pickActionTarget(resolved)
  if (!target) {
    showToast('warning', 'No service line available for refund.')
    return
  }
  refundTarget.value = { order: resolved, line: target.line, lineIndex: target.lineIndex }
  const ok = await loadRefundContextForTarget(resolved, target.lineIndex)
  if (!ok) {
    refundTarget.value = null
    return
  }
  refundModalOpen.value = true
}

function onDetailPanelRefund(order: OrderCardDTO): void {
  void openRefundModal(order)
}

function closeRefundModal(): void {
  if (refundSaving.value || refundContextLoading.value) return
  refundModalOpen.value = false
  refundTarget.value = null
  resetRefundContextDisplay()
}

function onRefundToast(payload: { type: 'success' | 'warning' | 'error'; message: string }): void {
  showToast(payload.type, payload.message)
}

async function confirmRefundModal(payload: RefundSubmitPayload): Promise<void> {
  if (refundSaving.value) return
  const target = refundTarget.value
  if (!target) return
  const pk = target.order.orderPrimaryId
  if (pk == null || !Number.isFinite(pk) || pk <= 0) {
    showToast('error', 'Order id missing for refund')
    return
  }

  refundSaving.value = true
  try {
    const response = await postCareOrderRefund({
      id: pk,
      line_index: target.lineIndex,
      amount: payload.amount,
      tax_type: payload.taxMode,
      note: payload.note,
    })
    if (response.code !== 200) {
      showToast('error', response.msg || 'Refund failed')
      return
    }
    refundModalOpen.value = false
    refundTarget.value = null
    resetRefundContextDisplay()
    showToast('success', 'Successfully refunded')
    await refreshOverlayOrder(target.order)
  } catch (e: unknown) {
    showToast('error', apiErrorMessage(e))
  } finally {
    refundSaving.value = false
  }
}

async function handleMobileClick(row: CouponUsageLogRow): Promise<void> {
  if (detailResolving.value) return
  const mobile = row.userMobile.trim()
  if (!mobile && !row.userId.trim()) {
    showToast('warning', 'User not found.')
    return
  }

  detailResolving.value = true
  try {
    const userId = await resolveUserDetailId(row)
    if (!userId) {
      showToast('warning', 'User not found.')
      return
    }

    // 不再预拉 detail；交由 UserDetail 只请求一次详情
    currentUserId.value = userId
    showUserDetail.value = true
  } finally {
    detailResolving.value = false
  }
}

function handleUserDetailClose(): void {
  showUserDetail.value = false
  currentUserId.value = ''
}

function onDocClick(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (
    target.closest('.search-select-wrapper') ||
    target.closest('.dropdown-menu') ||
    target.closest('.date-input-wrap') ||
    target.closest('.calendar-pop')
  ) {
    return
  }
  closeOverlays()
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  void initPage()
})

async function initPage(): Promise<void> {
  await fetchUsageMeta()
  await fetchUsageList()
}

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
})
</script>

<style scoped>
.coupon-usage-log-container {
  width: 100%;
  padding: 0;
  height: auto;
  overflow: visible;
  box-sizing: border-box;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0 0 16px;
  line-height: 26px;
}

.filter-section {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
  overflow: visible;
}

.filter-row {
  display: grid;
  grid-template-columns: 273px 273px auto;
  gap: 16px;
  align-items: center;
  width: 100%;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #ffffff;
  width: 273px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  flex-shrink: 0;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.search-wrapper:focus-within,
.search-wrapper.is-open {
  border-color: #666666;
}

.search-select-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 12px;
  cursor: pointer;
  user-select: none;
  position: relative;
  flex-shrink: 0;
}

.search-select-wrapper::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background-color: #e0e0e0;
}

.search-select-label {
  font-size: 14px;
  color: #000000;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
}

.dropdown-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
  pointer-events: none;
  transition: transform 0.2s ease;
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
}

.search-select-wrapper.is-open .dropdown-arrow {
  transform: rotate(180deg);
}

.search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  background: transparent;
  padding: 0 0 0 12px;
}

.search-input::placeholder {
  color: #cccccc;
  font-weight: 400;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: -1px;
  width: 273px;
  box-sizing: border-box;
  padding: 4px 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-item {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item.selected {
  font-weight: 600;
}

.filter-box {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  width: 273px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  flex-shrink: 0;
  padding: 0 12px;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.filter-box:focus-within,
.filter-box.is-open {
  border-color: #666666;
}

.filter-box--date {
  gap: 8px;
}

.date-calendar-anchor {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.date-input-wrap {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.filter-label-inline {
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
}

.filter-date-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  padding: 0;
  cursor: pointer;
}

.filter-date-input::placeholder {
  color: #cccccc;
  font-weight: 400;
}

.filter-date-input.is-placeholder {
  color: #cccccc;
  font-weight: 400;
  -webkit-text-fill-color: #cccccc;
}

.date-cal-split {
  flex-shrink: 0;
  width: 1px;
  height: 16px;
  margin: 0 8px;
  background: #e0e0e0;
}

.calendar-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex-shrink: 0;
  cursor: pointer;
}

.calendar-pop {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 35;
}

.filter-actions-inline {
  display: flex;
  align-items: center;
  gap: 16px;
  justify-self: end;
}

.btn-search {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #000000;
  color: #ffffff;
  border: none;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-search:hover {
  background-color: #484848;
}

.btn-clear {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #b0b0b0;
  color: #ffffff;
  border: none;
  border-radius: 16px;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-clear:hover {
  background-color: #cccccc;
}

.records-bar {
  margin-bottom: 16px;
}

.count-info {
  font-size: 16px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  min-height: 24px;
  line-height: 24px;
}

.count-info--pending {
  visibility: hidden;
}

.list-body {
  position: relative;
}

.orders-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
  box-sizing: border-box;
}

.orders-empty-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.orders-empty-msg {
  margin: 12px 0 0;
  font-size: 24px;
  font-weight: 500;
  color: #b0b0b0;
}

.table-section {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  overflow: hidden;
  box-sizing: border-box;
}

.usage-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.usage-table tr {
  height: 56px;
}

.usage-table th,
.usage-table td {
  box-sizing: border-box;
  height: 56px;
  min-height: 56px;
  max-height: 56px;
  padding: 0 12px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  white-space: nowrap;
  border: none;
  vertical-align: middle;
  background: #ffffff;
  box-shadow: inset 0 -1px 0 0 #e0e0e0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.usage-table th {
  font-weight: 700;
  text-align: center;
}

.usage-table th:first-child {
  border-radius: 16px 0 0 0;
}

.usage-table th:last-child {
  border-radius: 0 16px 0 0;
}

.usage-table td {
  font-weight: 400;
  text-align: center;
}

.usage-table tbody tr:last-child td {
  box-shadow: none;
}

.usage-table tbody tr:last-child td:first-child {
  border-radius: 0 0 0 16px;
}

.usage-table tbody tr:last-child td:last-child {
  border-radius: 0 0 16px 0;
}

.cell-link {
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  color: #000000;
  text-decoration: underline;
  text-decoration-color: #000000;
  text-underline-offset: 2px;
  cursor: pointer;
}

.cell-link:hover {
  color: #000000;
}

.pagination-wrapper {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.cod-overlay-root {
  position: fixed;
  inset: 0;
  z-index: 12400;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  padding: 24px 24px 40px;
}

.cod-overlay-shell {
  width: min(540px, 100%);
  height: calc(100vh - 64px);
  max-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* 作用于子组件 CareOrderDetailPanel 根节点 .cod-panel */
.cod-overlay-shell > :deep(*) {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  height: 100%;
}

@media (max-width: 1200px) {
  .filter-row {
    grid-template-columns: 273px 273px;
  }

  .filter-actions-inline {
    grid-column: 1 / -1;
    justify-self: end;
  }
}

@media (max-width: 720px) {
  .filter-row {
    grid-template-columns: 1fr;
  }

  .search-wrapper,
  .filter-box {
    width: 100%;
    max-width: 273px;
  }

  .dropdown-menu {
    width: 100%;
    max-width: 273px;
  }

  .filter-actions-inline {
    grid-column: auto;
    justify-self: stretch;
    justify-content: flex-end;
  }
}

.note-modal-root {
  position: fixed;
  inset: 0;
  z-index: 12500;
  background: rgba(55, 55, 55, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.note-modal {
  box-sizing: border-box;
  position: relative;
  width: min(530px, 100%);
  height: 380px;
  max-height: min(380px, calc(100vh - 48px));
  padding: 0 24px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  display: grid;
  grid-template-rows: 100px 200px 80px;
  overflow: hidden;
}

.note-modal-head {
  min-height: 0;
  padding: 0;
}

.note-modal-head-top {
  position: absolute;
  top: 24px;
  left: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
}

.note-modal-head h2 {
  margin: 0;
  font-weight: bold;
  font-size: 16px;
  color: #000000;
  line-height: 24px;
  white-space: nowrap;
}

.note-modal-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  padding: 4px;
  margin: -4px -4px 0 0;
  border-radius: 8px;
  flex-shrink: 0;
}

.note-modal-x:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.note-modal-x-ic {
  width: 14px;
  height: 14px;
  display: block;
  object-fit: contain;
}

.note-modal-label {
  position: absolute;
  top: 64px;
  left: 24px;
  z-index: 1;
  margin: 0;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  white-space: nowrap;
}

.note-modal-body {
  min-height: 0;
}

.note-modal-textarea {
  width: min(482px, 100%);
  height: 200px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 8px 16px 16px 12px;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  resize: none;
  outline: none;
  display: block;
}

.note-modal-textarea::placeholder {
  color: #cccccc;
}

.note-modal-textarea:focus {
  border-color: #666666;
}

.note-modal-textarea:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.note-modal-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  min-height: 0;
  background: #fff;
}

.note-modal-btn {
  box-sizing: border-box;
  width: 80px;
  min-width: 80px;
  height: 32px;
  padding: 4px 14px 4px 16px;
  border-radius: 16px;
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
}

.note-modal-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.note-modal-btn-outline {
  background: #fff;
  border: 1px solid #000000;
  color: #000000;
}

.note-modal-btn-outline:hover:not(:disabled) {
  background: #000000;
  color: #ffffff;
}

.note-modal-btn-solid {
  background: #000000;
  border: 1px solid #000000;
  color: #ffffff;
}

.note-modal-btn-solid:hover:not(:disabled) {
  background: #484848;
  border-color: #222222;
}
</style>
